import React, { useState, useEffect, useRef } from "react";
import { Box, alpha } from "@mui/material";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { useSelector } from "react-redux";
import OptionMultiChartSelector from "./MultiChart/OptionMultiChartSelector";
import OptionMultiChartContainer from "./MultiChart/OptionMultiChartContainer";
import OptionDepthChart from "./OptionDepthChart";

const OptionChart = () => {
  const { btncolor } = useSelector((state) => state);
  const [activeButton, setActiveButton] = useState("Price");
  const [chartLayout, setChartLayout] = useState(() => {
    try {
      return localStorage.getItem("options_chart_layout") || "1";
    } catch (e) {
      return "1";
    }
  });

  const [isFullscreen, setIsFullscreen] = useState(false);
  const chartWrapperRef = useRef(null);

  useEffect(() => {
    const handleLayoutUpdate = (e) => {
      if (e?.detail) {
        setChartLayout(e.detail);
      } else {
        const saved = localStorage.getItem("options_chart_layout") || "1";
        setChartLayout(saved);
      }
    };
    window.addEventListener("options_chart_layout_change", handleLayoutUpdate);
    return () => {
      window.removeEventListener("options_chart_layout_change", handleLayoutUpdate);
    };
  }, []);

  // Listen to browser native fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fsElem =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      const isFs = Boolean(
        fsElem &&
          chartWrapperRef.current &&
          (fsElem === chartWrapperRef.current || chartWrapperRef.current.contains(fsElem))
      );
      setIsFullscreen(isFs);

      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 100);
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 300);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, []);

  // Escape key fallback
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isFullscreen) {
        if (document.fullscreenElement && document.exitFullscreen) {
          document.exitFullscreen().catch(() => {});
        }
        setIsFullscreen(false);
        setTimeout(() => {
          window.dispatchEvent(new Event("resize"));
        }, 100);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen]);

  // Dispatch resize when fullscreen toggles
  useEffect(() => {
    const t1 = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 100);
    const t2 = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isFullscreen]);

  const handleToggleFullscreen = () => {
    if (!isFullscreen) {
      if (chartWrapperRef.current?.requestFullscreen) {
        chartWrapperRef.current.requestFullscreen().catch(() => {
          setIsFullscreen(true);
        });
      } else if (chartWrapperRef.current?.webkitRequestFullscreen) {
        chartWrapperRef.current.webkitRequestFullscreen();
      } else {
        setIsFullscreen(true);
      }
      setIsFullscreen(true);
    } else {
      if (document.fullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(() => {
            setIsFullscreen(false);
          });
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else {
          setIsFullscreen(false);
        }
      } else {
        setIsFullscreen(false);
      }
      setIsFullscreen(false);
    }
  };

  const handleSelectChartLayout = (newLayout) => {
    setChartLayout(newLayout);
    try {
      localStorage.setItem("options_chart_layout", newLayout);
    } catch (e) {}
  };

  const handleButtonClick = (button) => {
    setActiveButton(button);
  };

  return (
    <Box
      ref={chartWrapperRef}
      sx={{
        ...(isFullscreen
          ? {
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: "100vw",
              height: "100vh",
              zIndex: 99999,
              backgroundColor: "var(--body-color, #121212)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }
          : {
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }),
      }}
    >
      {/* Chart toggle buttons & Layout Header */}
      <Box
        sx={{
          ...chartToggleWrap,
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Box
            sx={{
              ...chartToggleBtn,
              backgroundColor:
                activeButton === "Price"
                  ? alpha(btncolor || "#1976d2", 0.15)
                  : "transparent",
              color:
                activeButton === "Price"
                  ? btncolor || "#1976d2"
                  : "var(--text-color)",
              borderColor:
                activeButton === "Price"
                  ? alpha(btncolor || "#1976d2", 0.4)
                  : "rgba(125,125,125,0.2)",
              fontWeight: activeButton === "Price" ? 700 : 500,
            }}
            onClick={() => handleButtonClick("Price")}
          >
            <ShowChartOutlinedIcon sx={{ fontSize: 14 }} />
            Price
          </Box>
          <Box
            sx={{
              ...chartToggleBtn,
              backgroundColor:
                activeButton === "Depth"
                  ? alpha(btncolor || "#1976d2", 0.15)
                  : "transparent",
              color:
                activeButton === "Depth"
                  ? btncolor || "#1976d2"
                  : "var(--text-color)",
              borderColor:
                activeButton === "Depth"
                  ? alpha(btncolor || "#1976d2", 0.4)
                  : "rgba(125,125,125,0.2)",
              fontWeight: activeButton === "Depth" ? 700 : 500,
            }}
            onClick={() => handleButtonClick("Depth")}
          >
            <BarChartOutlinedIcon sx={{ fontSize: 14 }} />
            Depth
          </Box>
        </Box>

        {/* Right side controls: Multi-Chart Selector and Fullscreen Expand button */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {activeButton === "Price" && (
            <OptionMultiChartSelector
              currentLayout={chartLayout}
              onSelectLayout={handleSelectChartLayout}
              btnColor={btncolor}
            />
          )}

          {/* Dedicated Expand / Fullscreen Button */}
          <Box
            component="button"
            type="button"
            title={isFullscreen ? "Exit Full Screen" : "Full Screen"}
            onClick={handleToggleFullscreen}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              px: "8px",
              py: "4px",
              borderRadius: "6px",
              border: "1px solid",
              borderColor: isFullscreen
                ? alpha(btncolor || "#1976d2", 0.6)
                : "rgba(125,125,125,0.2)",
              backgroundColor: isFullscreen
                ? alpha(btncolor || "#1976d2", 0.15)
                : "transparent",
              color: isFullscreen
                ? btncolor || "#1976d2"
                : "var(--text-color)",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 600,
              outline: "none",
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: alpha(btncolor || "#1976d2", 0.6),
                backgroundColor: alpha(btncolor || "#1976d2", 0.1),
              },
            }}
          >
            {isFullscreen ? (
              <>
                <FullscreenExitIcon sx={{ fontSize: 18 }} />
                <span style={{ fontSize: "11px", opacity: 0.9 }}>Exit Fullscreen</span>
              </>
            ) : (
              <>
                <FullscreenIcon sx={{ fontSize: 18 }} />
                <span style={{ fontSize: "11px", opacity: 0.9 }}>Expand</span>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Chart view */}
      <Box sx={{ flex: 1, minHeight: 0, width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        {activeButton === "Price" ? (
          <OptionMultiChartContainer
            layout={chartLayout}
            onLayoutChange={handleSelectChartLayout}
            btnColor={btncolor}
            isFullscreen={isFullscreen}
          />
        ) : (
          <OptionDepthChart isFullscreen={isFullscreen} />
        )}
      </Box>
    </Box>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────
const chartToggleWrap = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  px: "8px",
  py: "6px",
  borderBottom: "1px solid rgba(125,125,125,0.13)",
  backgroundColor: "var(--body-color-less2, #181a20)",
};

const chartToggleBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  px: "10px",
  py: "4px",
  borderRadius: "5px",
  border: "1px solid",
  cursor: "pointer",
  fontSize: "12px",
  transition: "all 0.15s ease",
  userSelect: "none",
};

export default OptionChart;
